import { useState, useEffect, useMemo, useRef } from "react"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Label } from "@/modules/shared/ui/components/label"
import {
    RadioGroup,
    RadioGroupItem,
} from "@/modules/shared/ui/components/radio-group"
import { Input } from "@/modules/shared/ui/components/input"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/modules/shared/ui/components/popover"
import { ZoomableCalendar as CalendarComponent } from "@/modules/shared/ui/components/zoomable-calendar"
import { Search, X, User, Calendar, Info } from "lucide-react"
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { cn } from "@/lib/utils"
import { useDispatch } from "react-redux"
import { AppDispatch } from "@/store"
import { toast } from "sonner"
import { useEmployeeService } from "@/modules/onboarding/employee/hooks/use-employee"
import type { EmployeeDto } from "@/modules/onboarding/employee/dtos"
import type { WorkflowDto } from "../../dtos/workflow.dto"
import { startWorkflow } from "../../store/workflowSlice"

type ContextType = "Standalone" | "Employee"

function addBusinessDays(startDate: Date, days: number): Date {
    let result = new Date(startDate)
    let remaining = days

    while (remaining > 0) {
        result = new Date(result.getTime() + 24 * 60 * 60 * 1000)
        if (result.getDay() !== 0 && result.getDay() !== 6) {
            remaining--
        }
    }

    return result
}

interface StartWorkflowDialogProps {
    workflow: WorkflowDto | null
    open: boolean
    onClose: () => void
    onStarted?: () => void
}

export function StartWorkflowDialog({
    workflow,
    open,
    onClose,
    onStarted,
}: StartWorkflowDialogProps) {
    const dispatch = useDispatch<AppDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [contextType, setContextType] = useState<ContextType>("Employee")
    const [batchName, setBatchName] = useState("")
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([])
    const [employeeSearch, setEmployeeSearch] = useState("")
    const [taskDateOverrides, setTaskDateOverrides] = useState<
        Record<string, Date | undefined>
    >({})
    const employeesLoadedRef = useRef(false)

    const {
        employees,
        isLoading: employeesLoading,
        loadEmployees,
    } = useEmployeeService()

    useEffect(() => {
        if (open && contextType === "Employee" && !employeesLoadedRef.current) {
            loadEmployees()
            employeesLoadedRef.current = true
        }
    }, [open, contextType, loadEmployees])

    const filteredEmployees = useMemo(() => {
        if (!employeeSearch.trim()) return employees
        const term = employeeSearch.toLowerCase()
        return employees.filter(
            (emp: EmployeeDto) =>
                emp.firstName.toLowerCase().includes(term) ||
                emp.lastName.toLowerCase().includes(term) ||
                emp.email.toLowerCase().includes(term),
        )
    }, [employees, employeeSearch])

    const toggleEmployee = (employeeId: string) => {
        setSelectedEmployeeIds((prev) =>
            prev.includes(employeeId)
                ? prev.filter((id) => id !== employeeId)
                : [...prev, employeeId],
        )
    }

    const removeEmployee = (employeeId: string) => {
        setSelectedEmployeeIds((prev) => prev.filter((id) => id !== employeeId))
    }

    const selectedEmployees = useMemo(
        () =>
            employees.filter((emp: EmployeeDto) =>
                selectedEmployeeIds.includes(emp.id),
            ),
        [employees, selectedEmployeeIds],
    )

    if (!workflow) return null

    const handleStart = async () => {
        if (!workflow) return

        if (contextType === "Employee" && selectedEmployeeIds.length === 0) {
            toast.error("Please select at least one employee")
            return
        }

        setIsSubmitting(true)

        try {
            if (contextType === "Employee") {
                // Generate batch ID if multiple employees selected
                const batchId =
                    selectedEmployeeIds.length > 1 ? crypto.randomUUID() : undefined
                const finalBatchName =
                    selectedEmployeeIds.length > 1 ? batchName || undefined : undefined

                // Prepare task date overrides
                const taskDueDateOverrides = Object.entries(taskDateOverrides)
                    .filter(([_, date]) => date !== undefined)
                    .map(([workflowTaskId, date]) => ({
                        workflowTaskId,
                        dueDate: date!.toISOString().split("T")[0], // Format as YYYY-MM-DD
                    }))

                // Start workflow for each selected employee
                const promises = selectedEmployeeIds.map((employeeId) =>
                    dispatch(
                        startWorkflow({
                            workflowId: workflow.id,
                            contextType: "Employee",
                            employeeId,
                            contextDescription: undefined,
                            batchId,
                            batchName: finalBatchName,
                            taskDueDateOverrides:
                                taskDueDateOverrides.length > 0
                                    ? taskDueDateOverrides
                                    : undefined,
                        }),
                    ).unwrap(),
                )

                await Promise.all(promises)
                toast.success(
                    `Workflow started for ${selectedEmployeeIds.length} employee(s)`,
                )
            } else {
                // Prepare task date overrides
                const taskDueDateOverrides = Object.entries(taskDateOverrides)
                    .filter(([_, date]) => date !== undefined)
                    .map(([workflowTaskId, date]) => ({
                        workflowTaskId,
                        dueDate: date!.toISOString().split("T")[0], // Format as YYYY-MM-DD
                    }))

                // Start standalone workflow
                await dispatch(
                    startWorkflow({
                        workflowId: workflow.id,
                        contextType: "Standalone",
                        taskDueDateOverrides:
                            taskDueDateOverrides.length > 0
                                ? taskDueDateOverrides
                                : undefined,
                        employeeId: undefined,
                        contextDescription: undefined,
                        batchId: undefined,
                        batchName: undefined,
                    }),
                ).unwrap()

                toast.success("Workflow started successfully")
            }

            onStarted?.()
            onClose()
            setSelectedEmployeeIds([])
            setBatchName("")
            setEmployeeSearch("")
        } catch (error) {
            console.error("Failed to start workflow:", error)
            toast.error((error as string) || "Failed to start workflow")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[800px] bg-card">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <DialogTitle className="text-2xl">Start Workflow</DialogTitle>
                        {workflow.isActive && (
                            <Badge variant="default" className="bg-green-100 text-green-700">
                                Active
                            </Badge>
                        )}
                    </div>
                    <DialogDescription className="text-left">
                        Configure and launch "{workflow.name}" workflow
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4">
                    <div className="space-y-2">
                        <h3 className="font-semibold text-foreground">Workflow Details</h3>
                        <p className="text-sm text-muted-foreground">
                            {workflow.description || "No description provided"}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>Tasks: {workflow.tasks?.length ?? 0}</span>
                            {workflow.updatedAt && (
                                <span>
                                    Updated: {new Date(workflow.updatedAt).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <h3 className="font-semibold text-foreground mb-3">
                                Workflow Context
                            </h3>
                            <RadioGroup
                                value={contextType}
                                onValueChange={(value) => setContextType(value as ContextType)}
                            >
                                <div className="space-y-3">
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setContextType("Employee")}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault()
                                                setContextType("Employee")
                                            }
                                        }}
                                        className={cn(
                                            "flex items-start space-x-3 border rounded-lg p-4 hover:bg-muted transition-colors cursor-pointer",
                                            contextType === "Employee" &&
                                            "border-primary bg-primary/5",
                                        )}
                                    >
                                        <RadioGroupItem value="Employee" id="employee" />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <Label
                                                    htmlFor="employee"
                                                    className="font-medium text-foreground cursor-pointer"
                                                >
                                                    Activate for Employee(s)
                                                </Label>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-muted-foreground hover:text-muted-foreground focus:outline-none"
                                                            aria-label="What is Activate for Employee(s)?"
                                                        >
                                                            <Info className="w-3.5 h-3.5" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top" className="max-w-xs">
                                                        Use this when the workflow belongs to specific
                                                        employees (e.g. onboarding, performance review).
                                                        Each selected employee gets their own workflow
                                                        instance you can track individually.
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Start this workflow for one or more employees in your
                                                organisation
                                            </p>
                                        </div>
                                    </div>

                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setContextType("Standalone")}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault()
                                                setContextType("Standalone")
                                            }
                                        }}
                                        className={cn(
                                            "flex items-start space-x-3 border rounded-lg p-4 hover:bg-muted transition-colors cursor-pointer",
                                            contextType === "Standalone" &&
                                            "border-primary bg-primary/5",
                                        )}
                                    >
                                        <RadioGroupItem value="Standalone" id="standalone" />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-1.5">
                                                <Label
                                                    htmlFor="standalone"
                                                    className="font-medium text-foreground cursor-pointer"
                                                >
                                                    Standalone Workflow
                                                </Label>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-muted-foreground hover:text-muted-foreground focus:outline-none"
                                                            aria-label="What is a Standalone Workflow?"
                                                        >
                                                            <Info className="w-3.5 h-3.5" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top" className="max-w-xs">
                                                        Use this for one-off workflows that aren't tied to
                                                        a specific employee — e.g. company-wide tasks,
                                                        project setup, or internal processes.
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                Start this workflow independently without linking to an
                                                employee
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </RadioGroup>
                        </div>

                        {contextType === "Employee" && (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="batchName">
                                        Batch Name <span className="text-muted-foreground">(Optional)</span>
                                    </Label>
                                    <Input
                                        id="batchName"
                                        value={batchName}
                                        onChange={(e) => setBatchName(e.target.value)}
                                        placeholder="e.g., Q1 2026 Engineering Hires"
                                        className="w-full"
                                    />
                                    {selectedEmployees.length > 1 && (
                                        <p className="text-xs text-muted-foreground">
                                            Group these {selectedEmployees.length} workflow instances
                                            together for easier tracking
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <Label>Select Employees</Label>
                                    <p className="text-sm text-muted-foreground mb-3">
                                        Choose one or more employees to start this workflow for
                                    </p>

                                    {selectedEmployees.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {selectedEmployees.map((emp: EmployeeDto) => (
                                                <Badge
                                                    key={emp.id}
                                                    variant="secondary"
                                                    className="px-3 py-1 flex items-center gap-2"
                                                >
                                                    <User className="w-3 h-3" />
                                                    <span>
                                                        {emp.firstName} {emp.lastName}
                                                    </span>
                                                    <Button
                                                        type="button"
                                                        onClick={() => removeEmployee(emp.id)}
                                                        className="hover:bg-muted rounded-full p-0.5 transition-colors"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </Button>
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                    <div className="relative mb-2">
                                        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                                        <Input
                                            value={employeeSearch}
                                            onChange={(e) => setEmployeeSearch(e.target.value)}
                                            placeholder="Search by name or email..."
                                            className="pl-9"
                                        />
                                    </div>

                                    <div className="border rounded-lg max-h-60 overflow-y-auto">
                                        {employeesLoading ? (
                                            <div className="p-4 text-center text-sm text-muted-foreground">
                                                Loading employees...
                                            </div>
                                        ) : filteredEmployees.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-muted-foreground">
                                                No employees found
                                            </div>
                                        ) : (
                                            <div className="divide-y">
                                                {filteredEmployees.map((emp: EmployeeDto) => (
                                                    <div
                                                        key={emp.id}
                                                        className="flex items-center gap-3 p-3 hover:bg-muted cursor-pointer transition-colors"
                                                        onClick={() => toggleEmployee(emp.id)}
                                                    >
                                                        <Checkbox
                                                            checked={selectedEmployeeIds.includes(emp.id)}
                                                            onCheckedChange={() => toggleEmployee(emp.id)}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-sm text-foreground">
                                                                {emp.firstName} {emp.lastName}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground truncate">
                                                                {emp.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {selectedEmployeeIds.length > 0 && (
                                        <p className="text-sm text-muted-foreground mt-2">
                                            {selectedEmployeeIds.length} employee
                                            {selectedEmployeeIds.length === 1 ? "" : "s"} selected
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {workflow.tasks && workflow.tasks.length > 0 && (
                        <div className="border-t pt-6">
                            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Task Schedule & Date Overrides
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Default due dates calculated from today (business days only).
                                Click on a date to override it.
                            </p>
                            <div className="space-y-3 max-h-75 overflow-y-auto">
                                {[...workflow.tasks]
                                    .sort((a, b) => a.orderIndex - b.orderIndex)
                                    .map((task) => {
                                        const startDate = new Date()
                                        const defaultDueDate =
                                            task.dueAtOffsetDays != null && task.dueAtOffsetDays >= 0
                                                ? addBusinessDays(startDate, task.dueAtOffsetDays)
                                                : null

                                        const overrideDate = taskDateOverrides[task.id]
                                        const displayDate = overrideDate || defaultDueDate

                                        return (
                                            <div
                                                key={task.id}
                                                className="flex items-start justify-between gap-4 p-3 bg-muted rounded-lg border border-border"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm text-foreground">
                                                        {task.title}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Assigned to: {task.defaultAssignedToEmail || "N/A"}
                                                    </p>
                                                    {task.dueAtOffsetDays != null && (
                                                        <p className="text-xs text-muted-foreground mt-1">
                                                            Default: {task.dueAtOffsetDays} business day
                                                            {task.dueAtOffsetDays === 1 ? "" : "s"}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="text-right shrink-0">
                                                        {displayDate ? (
                                                            <>
                                                                <p
                                                                    className={cn(
                                                                        "text-sm font-medium",
                                                                        overrideDate
                                                                            ? "text-primary"
                                                                            : "text-foreground",
                                                                    )}
                                                                >
                                                                    {displayDate.toLocaleDateString("en-US", {
                                                                        month: "short",
                                                                        day: "numeric",
                                                                        year: "numeric",
                                                                    })}
                                                                </p>
                                                                {overrideDate && (
                                                                    <p className="text-xs text-primary">
                                                                        Custom date
                                                                    </p>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <p className="text-sm text-muted-foreground">
                                                                No due date
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className={cn(
                                                                    "h-8 w-8 p-0 border-border hover:bg-muted",
                                                                    overrideDate &&
                                                                    "border-primary/30 bg-primary/5 hover:bg-primary/10",
                                                                )}
                                                            >
                                                                <Calendar className="h-4 w-4" />
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            className="w-auto p-0 bg-card"
                                                            align="end"
                                                        >
                                                            <CalendarComponent
                                                                mode="single"
                                                                selected={overrideDate}
                                                                onSelect={(date) => {
                                                                    setTaskDateOverrides((prev) => ({
                                                                        ...prev,
                                                                        [task.id]: date,
                                                                    }))
                                                                }}
                                                                disabled={(date) =>
                                                                    date <
                                                                    new Date(new Date().setHours(0, 0, 0, 0))
                                                                }
                                                                className="rounded-md border shadow-md bg-card"
                                                            />
                                                            {overrideDate && (
                                                                <div className="p-2 border-t">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            setTaskDateOverrides((prev) => ({
                                                                                ...prev,
                                                                                [task.id]: undefined,
                                                                            }))
                                                                        }}
                                                                        className="w-full text-xs text-muted-foreground hover:text-foreground"
                                                                    >
                                                                        Reset to default
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                            </div>
                                        )
                                    })}
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleStart}
                        disabled={isSubmitting}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        {isSubmitting ? "Starting..." : "Start Workflow"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
